import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status"]
  static values = {
    accessToken: String,
    clientId: String,
    appId: String,
    connectionId: String,
    cancelUrl: String
  }

  connect() {
    this.loadPickerApi()
  }

  loadPickerApi() {
    if (window.google?.picker) {
      this.openPicker()
      return
    }
    if (window.gapi) {
      gapi.load("picker", () => this.openPicker())
      return
    }

    const script = document.createElement("script")
    script.src = "https://apis.google.com/js/api.js"
    script.async = true
    script.defer = true
    script.onload = () => gapi.load("picker", () => this.openPicker())
    script.onerror = () => this.showError("Failed to load Google Picker API. Please check your connection and try again.")
    document.head.appendChild(script)
  }

  openPicker() {
    const view = new google.picker.DocsView(google.picker.ViewId.FOLDERS)
      .setSelectFolderEnabled(true)
      .setIncludeFolders(true)

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(this.accessTokenValue)
      .setAppId(this.appIdValue)
      .setCallback((data) => this.handlePickerCallback(data))
      .build()

    picker.setVisible(true)
  }

  handlePickerCallback(data) {
    if (data.action === google.picker.Action.PICKED) {
      this.showConfirmation(data.docs[0])
    } else if (data.action === google.picker.Action.CANCEL) {
      this.showCancelled()
    }
  }

  showConfirmation(folder) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    this.statusTarget.innerHTML = `
      <svg class="w-12 h-12 text-success mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h2 class="card-title justify-center text-success">Folder Selected</h2>
      <p class="text-base-content/80 mb-2">
        <strong>${this.escapeHtml(folder.name)}</strong>
      </p>
      <p class="text-base-content/60 text-sm mb-6">Link this Google Drive folder to your workspace?</p>
      <form method="post" action="/google_drive/folder_links">
        <input type="hidden" name="authenticity_token" value="${csrfToken}">
        <input type="hidden" name="folder_link[connection_id]" value="${this.escapeHtml(this.connectionIdValue)}">
        <input type="hidden" name="folder_link[folder_id]" value="${this.escapeHtml(folder.id)}">
        <input type="hidden" name="folder_link[folder_name]" value="${this.escapeHtml(folder.name)}">
        <input type="hidden" name="folder_link[access_level]" value="readwrite">
        <div class="flex gap-2 justify-center">
          <a href="${this.escapeHtml(this.cancelUrlValue)}" class="btn btn-ghost">Cancel</a>
          <button type="submit" class="btn btn-primary">Link Folder</button>
        </div>
      </form>
    `
  }

  showCancelled() {
    this.statusTarget.innerHTML = `
      <svg class="w-12 h-12 text-warning mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h2 class="card-title justify-center">Selection Cancelled</h2>
      <p class="text-base-content/60 mb-6">No folder was selected. You can choose a folder at any time from your workspace settings.</p>
      <a href="${this.escapeHtml(this.cancelUrlValue)}" class="btn btn-primary">Back to Google Drive</a>
    `
  }

  showError(message) {
    this.statusTarget.innerHTML = `
      <svg class="w-12 h-12 text-error mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h2 class="card-title justify-center text-error">Error</h2>
      <p class="text-base-content/60 mb-6">${this.escapeHtml(message)}</p>
      <a href="${this.escapeHtml(this.cancelUrlValue)}" class="btn btn-primary">Back to Google Drive</a>
    `
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.appendChild(document.createTextNode(str || ""))
    return div.innerHTML
  }
}
